// Public (auth-free) Live Wall — temperature monitoring dashboard.
//
// Reached via a shareable link minted from the Facility Details page
// (the "Generate Link" button → POST /facilities/{uuid}/share-tokens).
// The URL carries an opaque, un-guessable token, NOT the facility uuid,
// so an attacker who reverse-engineers one URL can't pivot to other
// facilities.
//
// Visual design follows the standalone "Guardian Live Wall" reference:
//   – Brand-mark header with site label and large wall-clock
//   – Status-coloured tile borders, badges, and large readings
//   – Per-severity sub-tiles when a zone contains devices in mixed states
//   – Bottom marquee ticker streaming active excursions on a red bar
//
// Status colours:
//   ok     → green  (#2fbf71)
//   warn   → amber  (#f5a623)
//   crit   → red    (#ef4452) with a CSS-keyframe blink so excursions are
//                    visually obvious on a wall display
//   nodata → grey   (#64748b)

import { useEffect, useMemo, useState } from 'react'

import { Box, Typography, Button } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { ExternalLink, RefreshCw, ArrowLeft } from 'lucide-react'

import APIs from '../../api'
import { publicApiClient, PublicApiError } from '../../api/publicClient'

// Live wall styles — kept in a local style tag so the page is fully
// self-contained and renders the same in every theme. The CSS keyframes
// `alarm-blink` drives the blinking red animation on crit tiles and
// `lw-marquee` powers the bottom ticker.
const LOCAL_STYLES = `
@keyframes subtile-alarm-blink {
  0%, 49% { box-shadow: 0 0 0 1px #ef4452, 0 0 18px 2px rgba(239,68,82,0.5); }
  50%, 100% { box-shadow: 0 0 0 1px rgba(239,68,82,0.3), 0 0 6px 0 rgba(239,68,82,0.15); }
}
.lw-subtile-crit {
  animation: subtile-alarm-blink 1.05s steps(1, end) infinite;
}
.lw-tile { transition: transform .15s ease, box-shadow .15s ease; }
.lw-tile:hover { transform: translateY(-2px); }
.lw-pulse {
  animation: lw-pulse 1.6s ease-in-out infinite;
}
@keyframes lw-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
}
/* Breathing effect for WARNING pills — a slow, gentle inhale/exhale
   glow that makes the warning state visible without being as alarming
   as the crit blink. Uses box-shadow expansion + opacity rather than
   transform so it never nudges surrounding layout. */
.lw-warn-breathe {
  animation: lw-warn-breathe 2.2s ease-in-out infinite;
}
@keyframes lw-warn-breathe {
  0%, 100% {
    box-shadow: 0 0 0 1px #f5a623, 0 0 4px 1px rgba(245, 166, 29, 0.25);
    opacity: 1;
  }
  50% {
    box-shadow: 0 0 0 1px #f5a623, 0 0 18px 5px rgba(245, 166, 29, 0.6);
    opacity: 0.85;
  }
}
/* Breathing effect for the entire WARNING sub-tile — same gentle
   inhale/exhale as the pill, but scoped to the whole bordered card so
   the operator reads the tile as alive rather than just its label.
   The overflow: hidden on the sub-tile contains the outer glow so the
   neighbouring cards are not lit up. */
.lw-subtile-warn {
  animation: lw-subtile-warn-breathe 2.2s ease-in-out infinite;
}
@keyframes lw-subtile-warn-breathe {
  0%, 100% {
    box-shadow: 0 0 0 1px rgba(245, 166, 29, 0.35), 0 0 6px 1px rgba(245, 166, 29, 0.18);
  }
  50% {
    box-shadow: 0 0 0 1px rgba(245, 166, 29, 0.7), 0 0 22px 4px rgba(245, 166, 29, 0.55);
  }
}
/* Bottom marquee — track is duplicated so we can scroll -50% for a
   seamless infinite loop without a visible reset. */
.lw-marquee-track {
  display: flex;
  align-items: center;
  gap: 64px;
  white-space: nowrap;
  width: max-content;

  /* 🔴 CHANGED */
  height: 100%;

  /* ❌ REMOVE THIS */
  /* align-self: stretch; */

  animation: lw-marquee 60s linear infinite;
  will-change: transform;
}
@keyframes lw-marquee {
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
}
.lw-marquee-viewport {
  position: relative;
  overflow: hidden;
  flex: 1;
  min-width: 0;

  /* 🔴 CHANGED */
  min-height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.lw-marquee-track:hover { animation-play-state: paused; }
`

// Canonical status palette. Keys cover both lowercase server values
// (ok/warn/crit/nodata) and the titlecase aliases used by some clients.
const STATUS_META = {
  ok: {
    label: 'In range',
    color: '#2fbf71',
    soft: 'rgba(47,191,113,0.14)',
    bg: 'rgba(47,191,113,0.05)',
  },
  warn: {
    label: 'Warning',
    color: '#f5a623',
    soft: 'rgba(245,166,29,0.14)',
    bg: 'rgba(245,166,29,0.05)',
  },
  crit: {
    label: 'Critical',
    color: '#ef4452',
    soft: 'rgba(239,68,82,0.14)',
    bg: 'rgba(239,68,82,0.07)',
  },
  nodata: {
    label: 'No data',
    color: '#64748b',
    soft: 'rgba(100,116,139,0.14)',
    bg: 'rgba(100,116,139,0.04)',
  },
}

function normalizeStatus(raw) {
  const s = String(raw || '').toLowerCase()
  if (s === 'critical' || s === 'crit' || s === 'alarm' || s === 'excursion') return 'crit'
  if (s === 'warning' || s === 'warn') return 'warn'
  if (s === 'ok' || s === 'in_range' || s === 'in-range' || s === 'normal' || s === 'in range')
    return 'ok'
  return 'nodata'
}

// "Effective" status — overrides the server `status` for rooms that have
// nothing to show: no alarm profile, no active alarms, and no reading.
// The backend often labels these "OK" because nothing is wrong, but for
// an operator they're indistinguishable from no-data, so we render them
// as such (grey badge, em-dash reading, no profile row).
function effectiveStatus(room) {
  const alarms = Array.isArray(room?.activeAlarms) ? room.activeAlarms : []
  const hasProfile = !!room?.alarmProfileName
  const hasReading = !!room?.latestReading
  if (!hasProfile && alarms.length === 0 && !hasReading) return 'nodata'
  return normalizeStatus(room.status)
}

function formatReading(value, unit) {
  if (value === null || value === undefined || value === '') return '—'
  const num = Number(value)
  if (Number.isNaN(num)) return '—'
  // Trim trailing `.0` for whole-number readings (e.g. CO2 464 reads
  // "464 ppm", not "464.0 ppm") and separate value from unit with a
  // space so the marquee and tile reading render naturally.
  const formatted = Number.isInteger(num) ? num.toString() : num.toFixed(1)
  return unit ? `${formatted} ${unit}` : formatted
}

// Centralized sensor-slug → {unit, label} mapping. Any new metric
// (VOC, PM2.5, …) only needs a row here and every consumer picks it
// up — keeps the per-alarm detail line, the big-reading unit, and the
// marquee copy in sync so the UI never falls back to a misleading
// "Temperature / °C" when the actual alarm is on a different metric.
function sensorMeta(slug) {
  if (!slug) return { unit: '', sensorLabel: null }
  const map = {
    temperature: { unit: '°C', sensorLabel: 'Temperature' },
    humidity: { unit: '%', sensorLabel: 'Humidity' },
    co2: { unit: 'ppm', sensorLabel: 'CO₂' },
  }
  return map[slug] || { unit: '', sensorLabel: null }
}

function formatClockTime(date) {
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
}

function formatDateLine(date) {
  const wk = date.toLocaleDateString([], { weekday: 'short' }).toUpperCase()
  const mo = date.toLocaleDateString([], { month: 'short' }).toUpperCase()
  const day = String(date.getDate()).padStart(2, '0')
  const yr = date.getFullYear()
  return `${wk} ${mo} ${day} ${yr} · IST`
}

function formatReceivedTime(iso) {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
  const mo = d.toLocaleDateString([], { month: 'short' })
  const day = String(d.getDate())
  return { time, label: `${time} · ${day} ${mo}` }
}

// Look up the device count for a given badge kind from the backend's
// `severityCounts` payload. The backend keys these in uppercase
// (WARNING / ALARM / OK); we map from the lowercase badge kind so the
// tile stays decoupled from wire casing.
//
// Returns `null` when the room carries no `severityCounts` at all —
// "the backend didn't tell us" is a different thing from a count of
// zero (a room whose devices aren't attached to any alarm profile
// legitimately reports 0). The badge decides what to do with that
// distinction; this helper just reports it faithfully.
function severityCountFor(room, kind) {
  const counts = room?.severityCounts
  if (!counts) return null
  const keyByKind = { warn: 'WARNING', crit: 'ALARM', ok: 'OK' }
  const key = keyByKind[kind]
  if (!key) return 0
  const raw = counts[key]
  const n = Number(raw)
  return Number.isFinite(n) ? n : 0
}

// Main tile badge — driven by the most severe ACTIVE ALARM, not by the
// count alone. A WARNING-severity alarm shows a yellow "WARNING" pill;
// a CRITICAL-severity alarm escalates to a red "CRITICAL" pill. The
// accompanying device count comes from `activeAlarms` (grouped by
// severity) so it stays consistent with what the sub-tile breakdown
// renders for the same room — `severityCounts` lags behind active
// alarms and would mis-report the count.
function badgeForColdRoom(room) {
  const status = effectiveStatus(room)
  const activeAlarms = Array.isArray(room.activeAlarms) ? room.activeAlarms : []

  if (activeAlarms.length > 0) {
    // Count by severity from activeAlarms directly — severityCounts can
    // be stale when alarms are still active. Keeping this derivation in
    // sync with getSubSections means the badge count never disagrees
    // with the sub-tile counts.
    let critCount = 0
    let warnCount = 0
    for (const alarm of activeAlarms) {
      const sev = normalizeStatus(alarm.severity)
      if (sev === 'crit') critCount++
      else if (sev === 'warn') warnCount++
    }
    if (critCount > 0) return { label: 'CRITICAL', count: critCount, kind: 'crit' }
    return { label: 'WARNING', count: warnCount, kind: 'warn' }
  }

  if (status === 'ok') {
    // The backend counts only the devices that are attached to an
    // alarm profile, so a room whose loggers aren't monitored by any
    // profile yet legitimately reports 0 — don't paper over that with
    // a "1 device" fallback. Only a payload with no `severityCounts`
    // at all falls back to 1 (a reading implies a device is there).
    const counted = severityCountFor(room, 'ok')
    const count = counted === null ? 1 : counted
    return { label: 'IN RANGE', count, kind: 'ok' }
  }
  if (status === 'warn') return { label: 'WARNING', count: 1, kind: 'warn' }
  if (status === 'crit') return { label: 'CRITICAL', count: 1, kind: 'crit' }
  return { label: 'NO DATA', count: 0, kind: 'nodata' }
}

// Severity breakdown for a zone, derived EXCLUSIVELY from `activeAlarms`.
//
// Every bucket here is backed by an actual active alarm, and each one
// carries that alarm's own device, value, profile and range. That
// invariant is the whole point: an operator reading the tile can trace
// any number on it back to a real alarm on a named device.
//
// There is deliberately NO "IN RANGE" bucket. `severityCounts.OK` counts
// placed devices that happen to have no active alarm, but those devices
// appear nowhere in `activeAlarms`, so an IN RANGE sub-tile could only
// ever show the room's latest reading and a count — implying a device
// was being monitored when nothing is actually being tracked for it. It
// also inflated the apparent device total: a room with one warning and
// one healthy logger used to read "2 devices" when only one was in
// alarm. Rooms with no active alarm fall through to the big-reading
// layout, which is the honest display for "nothing is wrong here".
//
// The backend's `severityCounts` is ignored for the same reason — it
// lags `activeAlarms` while an alarm is still open, so a CRITICAL alarm
// can read as 0 there while the alarm itself is plainly present.
//
// Returns an empty array when no alarms are active, which sends the
// renderer down the single big-reading path.
function getSubSections(room) {
  const activeAlarms = Array.isArray(room?.activeAlarms) ? room.activeAlarms : []

  // Group active alarms by severity, straight off the alarm rows.
  const critAlarms = []
  const warnAlarms = []
  for (const alarm of activeAlarms) {
    const sev = normalizeStatus(alarm.severity)
    if (sev === 'crit') critAlarms.push(alarm)
    else if (sev === 'warn') warnAlarms.push(alarm)
  }

  // One sub-tile per severity bucket that has at least one alarm.
  // Order is severe → mild so the worst-offending bucket is on top.
  // The render layer decides between the stacked sub-tile layout
  // (multiple buckets) and the single big-reading layout (one bucket).
  const sections = []
  if (critAlarms.length > 0) {
    sections.push({
      label: 'CRITICAL',
      kind: 'crit',
      count: critAlarms.length,
      alarms: critAlarms.map(formatAlarmDetail),
    })
  }
  if (warnAlarms.length > 0) {
    sections.push({
      label: 'WARNING',
      kind: 'warn',
      count: warnAlarms.length,
      alarms: warnAlarms.map(formatAlarmDetail),
    })
  }
  return sections
}

// Format one active alarm into the structured pieces the sub-tile
// render needs to mirror the big-reading layout (status pill → value →
// range → sensor type). Missing fields resolve to null rather than
// dangling strings so the render can skip them entirely.
//
//   {
//     sensorLabel: 'Temperature' | 'Humidity' | null,
//     sensorValue: 33 | null,
//     unit: '°C' | '%',
//     rangeText: '+2 to +8 °C' | null,
//   }
function formatAlarmDetail(alarm) {
  const sensorSlug = alarm?.currentValue?.sensorSlug
  const sensorValue = alarm?.currentValue?.sensorValue
  const minVal = Number(alarm?.minValue)
  const maxVal = Number(alarm?.maxValue)

  // Unit follows the alarm's metric, not the room's dominant metric —
  // a CRITICAL temperature alarm and a WARNING humidity alarm can
  // coexist in the same cold room, so we can't borrow the tile-level
  // unit and expect both detail lines to read sensibly.
  const { unit, sensorLabel } = sensorMeta(sensorSlug)

  // Trailing `.0` is noisy for whole-number limits; trim it. Prefix `+`
  // for non-negative values to match the standalone wall design
  // (`+2 to +8 °C`, `+35 to +70 %`).
  const fmt = (n) => {
    if (!Number.isFinite(n)) return null
    const s = Number.isInteger(n) ? n.toString() : n.toFixed(1)
    return n >= 0 ? `+${s}` : s
  }
  const rangeText =
    Number.isFinite(minVal) && Number.isFinite(maxVal)
      ? `${fmt(minVal)} to ${fmt(maxVal)} ${unit}`
      : null
  const numericValue = Number(sensorValue)
  const valueText =
    sensorValue !== undefined && sensorValue !== null && Number.isFinite(numericValue)
      ? Number(numericValue.toFixed ? numericValue.toFixed(1) : numericValue)
      : null

  // Per-alarm device identity — the device that actually raised THIS
  // alarm. The backend doesn't populate it yet, so it resolves to empty
  // and the device line renders nothing. `cleanName` collapses every
  // falsy shape (`undefined`, `null`, `''`, whitespace) so the render
  // only ever checks one.
  //
  // An array because `DeviceLine` takes a list — a single alarm always
  // names exactly one device, but keeping the shape uniform means the
  // renderer doesn't have to care.
  const deviceName = cleanName(alarm?.deviceName)

  return {
    sensorLabel,
    sensorValue: valueText,
    unit,
    rangeText,
    // Per-alarm profile name. Each alarm carries its own profile in the
    // payload (e.g. "live wall testing 3" vs "Live wall testing 1" on
    // different sensors) so the slider can surface the right profile
    // alongside the value instead of borrowing the room-level one and
    // silently misrepresenting the alarm.
    profileName: alarm?.alarmProfileName || null,
    // Note this is NOT the same field as the room-level `deviceName`
    // that `normalizePayload` derives from `latestReading` — see the
    // note there.
    deviceNames: deviceName ? [deviceName] : [],
    deviceSerial: cleanName(alarm?.deviceSerial),
  }
}

// Trim a wire string and collapse blanks / non-strings to null, so
// callers only ever branch on one falsy value. A payload that hasn't
// shipped a field yet reads exactly like one that sent it empty —
// which is what keeps the "device line absent" fallback honest.
function cleanName(value) {
  if (value === null || value === undefined) return null
  const s = String(value).trim()
  return s ? s : null
}

// Horizontal auto-slider used when a single severity bucket (or the
// big non-mixed tile) holds more than one alarm. One slide is visible
// at a time; the track auto-advances every 2s on a 0.5s slide, pauses
// on hover so an operator can read without losing their place, and
// renders dot indicators below so the operator can see at a glance how
// many alarms are queued up. With a single row the indicators are
// suppressed and the slide is just rendered in place — there's no
// point running the timer or showing "1/1" chrome.
//
// Why horizontal: when a cold room has two WARNING alarms on different
// sensors (e.g. one on temperature +30/+40 and another on humidity
// +35/+70), stacking them vertically pushes the tile tall enough that
// the grid starts to scroll on a wall display. The slider keeps each
// tile at a fixed height regardless of how many alarms it holds.
function MultiAlarmSlider({ rows, color, kind, isBig = false }) {
  const [activeIndex, setActiveIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)

  // Reset to first slide when the row count changes (a new alarm
  // appeared, or one cleared). Without this, advancing past the new
  // count would briefly leave a blank slide visible.
  useEffect(() => {
    setActiveIndex(0)
  }, [rows.length])

  // Auto-advance timer. Skip when there's only one row (no point
  // cycling) or when the operator is hovering (so they can read).
  useEffect(() => {
    if (rows.length <= 1 || isPaused) return undefined
    const delay = isBig ? 2000 : 2000
    const id = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % rows.length)
    }, delay)
    return () => clearInterval(id)
  }, [rows.length, isPaused, isBig])

  return (
    <Box
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      sx={{ position: 'relative', overflow: 'hidden' }}
    >
      <Box
        sx={{
          display: 'flex',
          transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: `translateX(-${activeIndex * 100}%)`,
        }}
      >
        {rows.map((row, rIdx) => (
          <Box
            key={rIdx}
            sx={{
              minWidth: '100%',
              flexShrink: 0,
            }}
          >
            <AlarmSlideContent row={row} color={color} kind={kind} isBig={isBig} />
          </Box>
        ))}
      </Box>
      {rows.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 0.5,
            mt: isBig ? 1.5 : 0.5,
          }}
        >
          {rows.map((_, idx) => (
            <Box
              key={idx}
              sx={{
                // Active dot is wider (20 / 14 px) so the eye lands on
                // it; inactive dots are short stubs that read as a
                // divider rather than competing with the value above.
                width: idx === activeIndex ? (isBig ? 20 : 14) : 6,
                height: 3,
                borderRadius: 1.5,
                backgroundColor: idx === activeIndex ? color : `${color}55`,
                transition: 'all 0.3s ease',
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

// Renders the content of a single slide. `isBig` picks the typographic
// scale: big mode matches the non-mixed tile (48 px value, full body
// sizes); small mode matches the mixed sub-tile (22 px value, compact
// rows). One component handles both so the visual rhythm stays
// consistent across the page.
function AlarmSlideContent({ row, color, kind, isBig }) {
  const valueFontSize = isBig ? 48 : 22
  const unitFontSize = isBig ? 22 : 13
  const profileFontSize = isBig ? 13 : 12
  const rangeFontSize = isBig ? 12 : 11.5
  const valueColor = kind === 'ok' ? 'var(--heading-color)' : color

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: isBig ? 0.5 : 0.15 }}>
      {row.sensorValue !== null && row.sensorValue !== undefined && (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography
            sx={{
              fontFamily: 'var(--font-display)',
              fontSize: valueFontSize,
              fontWeight: 700,
              color: valueColor,
              lineHeight: 1,
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: '-0.02em',
            }}
          >
            {Number(row.sensorValue).toFixed(1)}
          </Typography>
          <Typography
            sx={{
              fontFamily: 'var(--font-display)',
              fontSize: unitFontSize,
              fontWeight: 600,
              color: 'var(--text-muted)',
              lineHeight: 1,
            }}
          >
            {row.unit}
          </Typography>
        </Box>
      )}
      {row.profileName && (
        <Typography
          sx={{
            color: 'var(--heading-color)',
            fontSize: profileFontSize,
            fontWeight: 600,
            fontFamily: '"JetBrains Mono", ui-monospace, monospace',
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1.2,
          }}
        >
          {row.profileName}
        </Typography>
      )}
      {(row.rangeText || row.sensorLabel) && (
        <Typography
          sx={{
            color: kind === 'ok' ? 'var(--text-muted)' : color,
            fontSize: rangeFontSize,
            fontFamily: '"JetBrains Mono", ui-monospace, monospace',
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1.25,
          }}
        >
          {row.rangeText && (
            <Box
              component="span"
              sx={{
                color: 'var(--text-muted)',
                fontWeight: 500,
                fontSize: isBig ? 11.5 : 11,
                mr: 0.75,
                textTransform: 'none',
                letterSpacing: 0,
              }}
            >
              Range:
            </Box>
          )}
          {row.rangeText || '—'}
          {row.rangeText && row.sensorLabel && (
            <Box component="span" sx={{ color: 'var(--text-muted)', mx: 0.75 }}>
              ·
            </Box>
          )}
          {row.sensorLabel}
        </Typography>
      )}
      {/* Device identity sits last so the reading above never shifts. It
          renders nothing until the backend populates the field — see
          `DeviceLine`. */}
      <DeviceLine names={row.deviceNames} serial={row.deviceSerial} isBig={isBig} />
    </Box>
  )
}

// Device identity line under a reading. Renders NOTHING when the payload
// carries no device name — that early return is the entire behavioural
// switch that keeps this page pixel-identical against the current
// (device-less) backend response. Nothing else in the render path needs
// to know whether the field exists.
//
// `names` / `serial` arrive already normalised by `formatAlarmDetail`,
// which reads the alarm's own `deviceName` / `deviceSerial`. This
// component only joins and truncates.
//
// Shown at most two names, then a "+N more" tail — a room can hold a
// dozen loggers and a 320px tile has no room for a joined blob.
function DeviceLine({ names, serial, isBig = false }) {
  const list = (Array.isArray(names) ? names : [names]).filter(Boolean)
  const shown = list.slice(0, 2)
  const extra = list.length - shown.length
  const label = shown.join(' · ')
  // No name and no serial → render nothing at all. No wrapper element, no
  // margin: the line must leave zero trace in the layout, not just be
  // invisible.
  if (!label && !serial) return null

  return (
    <Typography
      sx={{
        color: 'var(--text-muted)',
        fontSize: isBig ? 12 : 11.5,
        fontFamily: '"JetBrains Mono", ui-monospace, monospace',
        lineHeight: 1.2,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      <Box component="span" sx={{ color: 'var(--text-muted)', fontWeight: 500, mr: 0.75 }}>
        Device:
      </Box>
      {label || serial}
      {label && serial && (
        <Box component="span" sx={{ color: 'var(--text-muted)', mx: 0.75 }}>
          ·
        </Box>
      )}
      {label ? serial : ''}
      {extra > 0 && ` · +${extra} more`}
    </Typography>
  )
}

// Placeholder note shown inside an IN RANGE tile.
//
// A room with nothing wrong has no reading to display, so the tile body
// would otherwise sit blank under the badge — which reads like a broken
// render rather than a deliberate "nothing to report". The note fills
// that space and is centred in it, so the eye lands on a statement
// instead of on leftover space.
function NoExcursionNote() {
  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography
        sx={{
          color: 'var(--text-muted)',
          fontSize: 13.5,
          fontWeight: 500,
          textAlign: 'center',
        }}
      >
        No Excursion Data Found
      </Typography>
    </Box>
  )
}

// Pick the most informative unit for the large reading. The backend payload
// carries the dominant metric in `latestReading.value.{temperature,humidity}`
// and a `sensorSlug` on the active alarm — prefer the slug when present.
function unitForColdRoom(room) {
  const slug = room?.activeAlarms?.[0]?.currentValue?.sensorSlug
  const fromSlug = sensorMeta(slug).unit
  if (fromSlug) return fromSlug
  // Default: °C for freezers/cold rooms, % if humidity-only payload.
  const v = room?.latestReading?.value || {}
  if (v.temperature === undefined && v.humidity !== undefined) return '%'
  return '°C'
}

// Which metric the tile's reading represents, as an operator-facing
// label ("Temperature", "Humidity", "CO₂").
//
// Deliberately does NOT fall back to `latestReading.deviceName`. That used
// to, which conflated a device identity with a sensor label: for an
// alarm-free room the big layout renders this string twice — once as the
// profile line and once after the range — and now that those tiles carry a
// real device line it would have appeared a third time. The marquee keeps
// its own device fallback in `excursionLine`, where naming the device is
// genuinely what it wants.
function sensorForColdRoom(room) {
  const alarm = room?.activeAlarms?.[0]
  if (alarm?.currentValue?.sensorSlug) {
    const label = sensorMeta(alarm.currentValue.sensorSlug).sensorLabel
    if (label) return label
  }
  return null
}

// Format the active alarm's allowed range as `+10 to +25 °C` (with an
// explicit `+` for non-negative bounds, matching the standalone reference
// design). The min/max strings come back from the API as quoted numerics
// (e.g. `"10.00"`) so we coerce defensively and fall back to `—` for any
// missing or non-numeric bound.
function rangeForColdRoom(room, unit) {
  const alarm = room?.activeAlarms?.[0]
  if (!alarm) return null
  const minRaw = alarm.minValue
  const maxRaw = alarm.maxValue
  if (minRaw === null || minRaw === undefined || maxRaw === null || maxRaw === undefined) {
    return null
  }
  const min = Number(minRaw)
  const max = Number(maxRaw)
  if (Number.isNaN(min) || Number.isNaN(max)) return null
  // Trailing `.0` would be noisy for whole-number limits; trim it. We
  // prefix `+` to non-negative values so the range reads consistently
  // with the standalone reference (e.g. `+0 to +70 %`, `+2 to +8 °C`).
  const fmt = (n) => {
    const s = Number.isInteger(n) ? n.toString() : n.toFixed(1)
    return n >= 0 ? `+${s}` : s
  }
  return `${fmt(min)} to ${fmt(max)} ${unit || '°C'}`
}

function normalizePayload(data) {
  if (!data) return null

  const coldRooms = Array.isArray(data.coldRooms)
    ? data.coldRooms.map((room) => ({
        ...room,
        status: String(room.status || 'nodata').toLowerCase(),
      }))
    : []

  // Derive the reading/unit per room once so render is cheap.
  const decorated = coldRooms.map((room) => {
    const reading = room.latestReading
    const value = reading?.value || {}
    const alarmCurrent = room?.activeAlarms?.[0]?.currentValue || {}
    const slug = alarmCurrent.sensorSlug
    // Prefer the latestReading value for the metric, but fall back to
    // the active alarm's own currentValue when the room has no
    // latestReading yet (typical for a freshly-tripped alarm). Without
    // this, a room that's warning on CO2 with sensorValue=464 and no
    // latestReading would render "—" everywhere — the operator gets no
    // sense of HOW far out of bounds the reading is.
    const alarmValue = alarmCurrent.sensorValue ?? null
    const sensorValue =
      slug === 'humidity'
        ? (value.humidity ?? alarmValue ?? null)
        : slug === 'temperature'
          ? (value.temperature ?? alarmValue ?? null)
          : slug === 'co2'
            ? (value.co2 ?? alarmValue ?? null)
            : (value.temperature ?? value.humidity ?? alarmValue ?? null)
    return {
      ...room,
      sensorValue,
      unit: unitForColdRoom(room),
      receivedAt: reading?.valueAt || null,
      // Room-level device name — whichever placed device sent the NEWEST
      // packet. NOT the same field as `activeAlarms[i].deviceName`, which
      // is the device that actually raised that alarm. Keep the two apart:
      // this one is only correct when a room holds a single device, and
      // it is used solely as the marquee's last-resort label.
      deviceName: reading?.deviceName || '',
    }
  })

  // Header tally is always computed from `effectiveStatus`, never from
  // the backend's `summary.{okCount,warningCount,alarmCount}`. The
  // backend labels every unconfigured room (no profile, no reading) as
  // OK, but the frontend overrides those to `nodata` for tile rendering
  // — so any counts derived from `effectiveStatus` agree with the tiles,
  // not the wire summary.
  return {
    ...data,
    coldRooms: decorated,
  }
}

// Build a single line of text describing an active excursion, used by
// the bottom marquee. We avoid technical IDs we don't have on the wire
// and stick to operator-readable copy: "Sensor — Range — reading".
// The marquee pill block already shows the zone name to the left of
// this line, so starting the line with the name would duplicate it.
//
// This is where the device-name fallback belongs after `sensorForColdRoom`
// stopped conflating the two: on an alarm-free zone the ticker has no
// sensor label to show, and naming the device is more useful to an
// operator than a generic "Temperature".
function excursionLine(room) {
  const unit = room.unit || '°C'
  const value = formatReading(room.sensorValue, unit)
  const range = rangeForColdRoom(room, unit)
  const sensor = sensorForColdRoom(room) || room.deviceName || 'Temperature'
  const parts = []
  if (sensor) parts.push(sensor)
  if (range) parts.push(`Range ${range}`)
  parts.push(`currently ${value}`)
  return parts.join(' · ')
}

function LiveWall() {
  // The share token is the only thing in the URL — pulled from the
  // `:token` path param of /live-wall/:token. Fall back to ?token=…
  // for environments that flatten the path (rare, but cheap to support).
  const [token] = useState(() => {
    if (typeof window === 'undefined') return ''
    const parts = window.location.pathname.split('/').filter(Boolean)
    if (parts[0] === 'live-wall' && parts[1]) return parts[1]
    return new URLSearchParams(window.location.search).get('token') || ''
  })

  // Aggregate payload from the public endpoint. Polling every 30s gives
  // the wall-display "live" feel without the operational complexity of
  // WebSockets on an auth-free surface. `staleTime` is shorter than the
  // poll so a foreground tab refetches immediately on focus.
  const {
    data: payload,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ['live-wall', token],
    queryFn: async () => {
      const res = await publicApiClient.get(APIs.PUBLIC.liveWall(token))
      return normalizePayload(res?.data?.data ?? res?.data ?? null)
    },
    enabled: !!token,
    refetchInterval: 30_000,
    staleTime: 10_000,
    retry: 1,
  })

  // Wall-clock — independent of server data so the timestamp keeps
  // ticking while a request is in flight or after a 410.
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(id)
  }, [])

  // Derive everything we render from the payload. Tolerates missing
  // fields (coldRooms / facility may be absent on a half-built server).
  const facility = payload?.facility
  const coldRooms = Array.isArray(payload?.coldRooms) ? payload.coldRooms : []

  // Two-line site block for the header:
  //   name: facility.name (e.g. "Air Wall testing")
  //   address: "Address: {address}, {city}, {country}"
  // Each piece falls back to a sensible default so we never render
  // "undefined" or "Address: , , ".
  const siteName = facility?.name || 'Site'
  const addressParts = [facility?.address, facility?.city, facility?.country].filter(Boolean)
  const siteAddress = addressParts.length ? `Address: ${addressParts.join(', ')}` : 'Address: —'

  // Active-excursion ticker. We surface both `crit` (blinking red) and
  // `warn` (yellow) so the operator sees pending issues before they tip
  // into a full excursion.
  const excursions = useMemo(
    () => coldRooms.filter((r) => effectiveStatus(r) === 'crit' || effectiveStatus(r) === 'warn'),
    [coldRooms]
  )

  // ── Empty / error states ───────────────────────────────────────────
  if (!token) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          backgroundColor: 'var(--body-bg)',
          color: 'var(--text-muted)',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" sx={{ color: 'var(--heading-color)' }}>
            Missing share token
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
            This page requires a shareable link generated from the Facility Details page.
          </Typography>
        </Box>
      </Box>
    )
  }

  if (error) {
    const isGone = error instanceof PublicApiError && error.code === 'gone'
    const isNotFound = error instanceof PublicApiError && error.code === 'notFound'
    const title = isGone
      ? 'This link is no longer active'
      : isNotFound
        ? 'Share link not found'
        : 'Could not load live data'
    const subtitle = isGone
      ? 'The owner revoked this link or it has expired. Ask them for a new one.'
      : isNotFound
        ? 'Check the URL — share links are opaque, so a typo will not resolve.'
        : 'The live wall backend is having trouble. Try again in a moment.'
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          backgroundColor: 'var(--body-bg)',
          color: 'var(--text-muted)',
          p: 4,
        }}
      >
        <style>{LOCAL_STYLES}</style>
        <Box sx={{ textAlign: 'center', maxWidth: 480 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              mx: 'auto',
              mb: 2,
              backgroundColor: isGone ? 'rgba(239,68,82,0.15)' : 'var(--chip-filled-bg)',
              color: isGone ? '#ef4452' : 'var(--text-muted)',
            }}
          >
            <Typography sx={{ fontSize: 22, fontWeight: 700 }}>!</Typography>
          </Box>
          <Typography variant="h5" sx={{ color: 'var(--heading-color)' }}>
            {title}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
            {subtitle}
          </Typography>
          {!isGone && !isNotFound && (
            <Button
              size="small"
              variant="outlinedNeutral"
              startIcon={<RefreshCw size={14} />}
              onClick={() => refetch()}
              sx={{ mt: 2 }}
            >
              Retry
            </Button>
          )}
        </Box>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'var(--body-bg)',
        color: 'var(--text-primary)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <style>{LOCAL_STYLES}</style>

      {/* ── Header ───────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2.5,
          px: 4,
          py: 2,
          borderBottom: '1px solid var(--divider-color)',
          backgroundColor: 'var(--header-bg)',
        }}
      >
        {/* Brand mark — red square with "MdG" wordmark to match the
            reference design. Falls back to a neutral block if the brand
            image is not supplied. */}
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 1.25,
            display: 'grid',
            placeItems: 'center',
            background: 'linear-gradient(135deg, #ef4452 0%, #b91c1c 100%)',
            color: '#fff',
            fontFamily: 'var(--font-display)',
            fontWeight: 800,
            fontSize: 13,
            letterSpacing: '-0.02em',
            flexShrink: 0,
          }}
        >
          MdG
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: 'var(--font-display)',
              fontSize: 18,
              fontWeight: 700,
              color: 'var(--heading-color)',
              lineHeight: 1.1,
            }}
          >
            Guardian Live Wall
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'var(--text-muted)',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              fontSize: 10.5,
              fontWeight: 600,
            }}
          >
            Cold Chain Surveillance
          </Typography>
        </Box>

        {/* Site block — facility name on top, "Address: …" line below.
            Sits between the brand mark and the tally chips. */}
        <Box
          sx={{
            ml: 2,
            pl: 2.5,
            borderLeft: '1px solid var(--divider-color)',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.25,
            minWidth: 0,
            color: 'var(--text-secondary)',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'var(--font-display)',
              fontSize: 15,
              fontWeight: 700,
              color: 'var(--heading-color)',
              lineHeight: 1.1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {siteName}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'var(--text-muted)',
              fontSize: 12,
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {siteAddress}
          </Typography>
        </Box>

        <Box sx={{ flex: 1 }} />

        {/* Wall clock — large time with date line below, mirrors the
            standalone reference. */}
        <Box
          sx={{
            ml: 1,
            px: 2,
            py: 0.75,
            borderRadius: 1.25,
            border: '1px solid var(--divider-color)',
            textAlign: 'right',
            minWidth: 130,
            backgroundColor: 'var(--card-bg)',
          }}
        >
          <Box
            sx={{
              fontFamily: '"JetBrains Mono", ui-monospace, monospace',
              fontSize: 22,
              fontWeight: 700,
              color: 'var(--heading-color)',
              lineHeight: 1,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatClockTime(now)}
          </Box>
          <Box
            sx={{
              color: 'var(--text-muted)',
              fontSize: 9.5,
              letterSpacing: '0.14em',
              mt: 0.5,
              fontWeight: 600,
            }}
          >
            {formatDateLine(now)}
          </Box>
        </Box>
      </Box>

      {/* ── Tile grid ───────────────────────────────────────────────── */}
      {isLoading ? (
        <Box sx={{ flex: 1, display: 'grid', placeItems: 'center', color: 'var(--text-muted)' }}>
          <Typography variant="caption">Loading live data…</Typography>
        </Box>
      ) : coldRooms.length === 0 ? (
        <Box sx={{ flex: 1, display: 'grid', placeItems: 'center', color: 'var(--text-muted)' }}>
          <Typography variant="caption">No cold rooms are shared in this link yet.</Typography>
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            p: 3,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))',
            gap: 2,
            alignContent: 'start',
          }}
        >
          {coldRooms.map((r) => {
            const status = effectiveStatus(r)
            const meta = STATUS_META[status]
            const badge = badgeForColdRoom(r)
            const badgeMeta = STATUS_META[badge.kind]
            const subSections = getSubSections(r)
            const received = formatReceivedTime(r.receivedAt)
            const profile = r.alarmProfileName
            const sensor = sensorForColdRoom(r)
            const range = rangeForColdRoom(r, r.unit)
            // Formatted per-alarm rows. Used by the non-mixed tile so a
            // room with multiple alarms of the same severity (the
            // "horizontal auto slider" case) can cycle through each
            // alarm's own value, profile, range and sensor instead of
            // silently dropping every alarm after the first.
            const allAlarmRows = (Array.isArray(r.activeAlarms) ? r.activeAlarms : []).map(
              formatAlarmDetail
            )
            // "Mixed" means the room has active alarms at more than one severity.
            // Single-bucket rooms collapse into the badge + big-reading
            // layout above; multi-bucket rooms (e.g. one CRITICAL and one
            // WARNING alarm on different sensors) render each bucket as
            // its own sub-tile.
            const isMixed = subSections.length >= 2
            // An IN RANGE room has nothing to report. On a wall display
            // the hero reading and the "Received" stamp are noise: the
            // operator's eye should land on the rooms that need
            // attention, not on a temperature that is behaving. The tile
            // keeps its name, the IN RANGE badge and the count of
            // monitored devices, so the grid still reads as "this room
            // is fine".
            const isInRange = badge.kind === 'ok'
            const readingValue = status === 'nodata' ? '—' : formatReading(r.sensorValue, r.unit)
            // Device identity for the big layout, which hand-rolls its
            // markup and so never routes through AlarmSlideContent. Taken
            // from the first active alarm; rooms with no alarms show no
            // device, since per `getSubSections` nothing appears on a
            // tile that isn't backed by one.
            const tileDevice = allAlarmRows[0] || { deviceNames: [], deviceSerial: null }
            const valueColor =
              status === 'nodata'
                ? 'var(--text-muted)'
                : status === 'crit'
                  ? '#ef4452'
                  : status === 'warn'
                    ? '#f5a623'
                    : 'var(--heading-color)'

            return (
              <Box
                key={r.uuid || r.name}
                className={`lw-tile lw-${status}`}
                sx={{
                  position: 'relative',
                  backgroundColor: meta.bg,
                  border: '1px solid var(--card-border)',
                  borderRadius: 2,
                  minHeight: 180,
                  p: '20px 22px 16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.25,
                  overflow: 'hidden',
                  cursor: 'default',
                }}
              >
                {/* Coloured top border — green / amber / red / grey */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    backgroundColor: meta.color,
                  }}
                />

                {/* Title + badge row — name on the left, status pill and
                    device count on the right. The pill is hidden when
                    the room has multiple severity buckets (mixed state);
                    each bucket gets its own sub-tile below, so a second
                    pill in the title row would be redundant. The pill
                    and count are separate so the count stays readable
                    when the label is long ("CRITICAL" vs "IN RANGE"). */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Typography
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      fontFamily: 'var(--font-display)',
                      fontSize: 17,
                      fontWeight: 700,
                      color: 'var(--heading-color)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      lineHeight: 1.15,
                    }}
                  >
                    {r.name || 'Unnamed zone'}
                  </Typography>
                  {!isMixed && (
                    <Box
                      sx={{
                        flexShrink: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                      }}
                    >
                      <Box
                        className={badge.kind === 'warn' ? 'lw-warn-breathe' : undefined}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          px: 1.1,
                          py: 0.45,
                          borderRadius: 1,
                          backgroundColor: badgeMeta.soft,
                          color: badgeMeta.color,
                          border: `1px solid ${badgeMeta.color}`,
                          fontSize: 10,
                          fontWeight: 800,
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          lineHeight: 1,
                          ...(badge.kind === 'crit' && {
                            animation: 'lw-pulse 1.6s ease-in-out infinite',
                          }),
                        }}
                      >
                        {badge.label}
                      </Box>
                      <Typography
                        sx={{
                          color: 'var(--text-muted)',
                          fontSize: 11.5,
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {badge.count} {badge.count === 1 ? 'device' : 'devices'}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Big reading + subtitle + range — hidden in mixed-state
                    tiles because the per-bucket sub-tiles already convey
                    the situation. Showing both would be redundant and
                    visually noisy. In-range tiles get a "no excursion"
                    note in place of the reading: there is nothing wrong
                    to report, but the tile still says so instead of
                    sitting blank. When the room carries multiple alarms
                    of the same severity (a "WARNING 2 devices" tile from
                    two sensors, say), we drop the static layout and feed
                    each alarm through the horizontal auto-slider so every
                    alarm is visible instead of just the first. */}
                {!isMixed &&
                  (isInRange ? (
                    <NoExcursionNote />
                  ) : (
                    <>
                      {allAlarmRows.length > 1 ? (
                        <MultiAlarmSlider
                          rows={allAlarmRows}
                          color={valueColor}
                          kind={status}
                          isBig={true}
                        />
                      ) : (
                        <>
                          <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                            <Typography
                              sx={{
                                fontFamily: 'var(--font-display)',
                                fontSize: 48,
                                fontWeight: 700,
                                color: valueColor,
                                lineHeight: 1,
                                fontVariantNumeric: 'tabular-nums',
                                letterSpacing: '-0.02em',
                              }}
                            >
                              {readingValue}
                            </Typography>
                          </Box>

                          {(profile || sensor) && (
                            <Typography
                              sx={{
                                color: 'var(--text-primary)',
                                fontWeight: 600,
                                fontSize: 13,
                                fontFamily: '"JetBrains Mono", ui-monospace, monospace',
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {profile || sensor}
                            </Typography>
                          )}

                          {(range || sensor) && (
                            <Typography
                              sx={{
                                color:
                                  status === 'crit'
                                    ? '#ef4452'
                                    : status === 'warn'
                                      ? '#f5a623'
                                      : 'var(--text-muted)',
                                fontSize: 12,
                                fontFamily: '"JetBrains Mono", ui-monospace, monospace',
                                fontVariantNumeric: 'tabular-nums',
                                mt: 0.25,
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  color: 'var(--text-muted)',
                                  fontWeight: 500,
                                  fontSize: 11.5,
                                  mr: 0.75,
                                  textTransform: 'none',
                                  letterSpacing: 0,
                                }}
                              >
                                Range:
                              </Box>
                              {range || '—'}
                              {sensor && (
                                <>
                                  <Box
                                    component="span"
                                    sx={{
                                      color: 'var(--text-muted)',
                                      mx: 0.75,
                                    }}
                                  >
                                    ·
                                  </Box>
                                  {sensor}
                                </>
                              )}
                            </Typography>
                          )}
                          {/* Device line for the hand-rolled big layout —
                            the one body path that doesn't share
                            AlarmSlideContent. Last, so the hero reading
                            never shifts; renders nothing until the
                            alarm's `deviceName` field ships. */}
                          <DeviceLine
                            names={tileDevice.deviceNames}
                            serial={tileDevice.deviceSerial}
                            isBig
                          />
                        </>
                      )}

                      {received && (
                        <Typography
                          variant="caption"
                          sx={{ color: 'var(--text-muted)', fontSize: 11.5, mt: 0.25 }}
                        >
                          Received {received.label}
                        </Typography>
                      )}
                    </>
                  ))}

                {/* Severity breakdown — one sub-tile per bucket that has at
                    least one active alarm. Each sub-tile mirrors the
                    single-bucket big-reading layout, just scaled down:
                    status pill → current value → device → alarm profile
                    name → range → sensor type. Only reached when a room
                    has alarms at two different severities; a single
                    bucket renders through the big-reading layout above
                    instead. */}
                {isMixed && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.25,
                      mt: 0.5,
                    }}
                  >
                    {subSections.map((s, idx) => {
                      const sMeta = STATUS_META[s.kind]

                      // One row per active alarm in this bucket. Every field comes off
                      // the alarm itself — its value, profile, range and
                      // device — so a slide can always be traced back to
                      // a specific alarm on a specific logger. There is
                      // no IN RANGE branch here by design: buckets only
                      // exist when they have at least one alarm (see
                      // `getSubSections`).
                      const readingRows = (Array.isArray(s.alarms) ? s.alarms : []).map((a) => ({
                        sensorLabel: a.sensorLabel,
                        sensorValue: a.sensorValue,
                        unit: a.unit,
                        rangeText: a.rangeText,
                        profileName: a.profileName,
                        deviceNames: a.deviceNames,
                        deviceSerial: a.deviceSerial,
                      }))

                      return (
                        <Box
                          key={`${s.kind}-${idx}`}
                          className={
                            s.kind === 'crit'
                              ? 'lw-subtile-crit'
                              : s.kind === 'warn'
                                ? 'lw-subtile-warn'
                                : undefined
                          }
                          sx={{
                            position: 'relative',
                            backgroundColor: sMeta.soft,
                            border: `1px solid ${sMeta.color}55`,
                            borderRadius: 1.25,
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              height: 3,
                              backgroundColor: sMeta.color,
                            }}
                          />
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              px: 1.25,
                              py: 1,
                            }}
                          >
                            <Box
                              className={s.kind === 'warn' ? 'lw-warn-breathe' : undefined}
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                px: 1.1,
                                py: 0.45,
                                borderRadius: 1,
                                backgroundColor: sMeta.soft,
                                color: sMeta.color,
                                border: `1px solid ${sMeta.color}`,
                                fontSize: 9.5,
                                fontWeight: 800,
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                                lineHeight: 1,
                                ...(s.kind === 'crit' && {
                                  animation: 'lw-pulse 1.6s ease-in-out infinite',
                                }),
                              }}
                            >
                              {s.label}
                            </Box>
                            <Box sx={{ flex: 1 }} />
                            <Typography
                              sx={{
                                color: 'var(--text-muted)',
                                fontSize: 11.25,
                                fontWeight: 500,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {s.count} {s.count === 1 ? 'device' : 'devices'}
                            </Typography>
                          </Box>

                          {/* Per-row reading block — mirrors the
                              single-bucket layout: value → profile →
                              range · type. A bucket with multiple
                              alarms (e.g. two CRITICALs on different
                              sensors) feeds the rows through the
                              horizontal auto-slider so each alarm
                              gets its own visible slot instead of
                              being squeezed into a stacked list that
                              grows the tile. A single row is rendered
                              inline by AlarmSlideContent to keep the
                              visual rhythm consistent. */}
                          <Box
                            sx={{
                              px: 1.25,
                              pb: 1,
                              pt: 0.25,
                            }}
                          >
                            {readingRows.length > 1 ? (
                              <MultiAlarmSlider
                                rows={readingRows}
                                color={sMeta.color}
                                kind={s.kind}
                                isBig={false}
                              />
                            ) : (
                              readingRows.length === 1 && (
                                <AlarmSlideContent
                                  row={readingRows[0]}
                                  color={sMeta.color}
                                  kind={s.kind}
                                  isBig={false}
                                />
                              )
                            )}
                          </Box>
                        </Box>
                      )
                    })}
                  </Box>
                )}
              </Box>
            )
          })}
        </Box>
      )}

      {/* ── Bottom marquee + action bar ────────────────────────────── */}
      <Box
        sx={{
          borderTop: '1px solid var(--divider-color)',
          backgroundColor: 'var(--header-bg)',
          display: 'flex',
          alignItems: 'stretch',

          // 🔴 CHANGED
          minHeight: 52,
          height: 52,
        }}
      >
        {/* Red "ACTIVE EXCURSIONS" marker — fixed width so it overlays
            the marquee on the left, mirroring the standalone reference. */}
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2.5,
            backgroundColor: '#ef4452',
            color: '#fff',
            fontWeight: 800,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            fontSize: 13,
            position: 'relative',
            zIndex: 2,
            boxShadow: '4px 0 12px -2px rgba(0,0,0,0.4)',
          }}
        >
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              backgroundColor: '#fff',
              animation: 'lw-pulse 1.6s ease-in-out infinite',
            }}
          />
          <Box component="span">Active Excursions</Box>
          <Box
            component="span"
            sx={{
              fontFamily: '"JetBrains Mono", ui-monospace, monospace',
              fontWeight: 800,
            }}
          >
            ·&nbsp;{excursions.length}
          </Box>
        </Box>

        {/* Scrolling marquee — duplicated content so the -50% translate
            loops seamlessly. Falls back to a static message when there
            are no active excursions. */}
        <Box className="lw-marquee-viewport">
          {excursions.length === 0 ? (
            <Box
              sx={{
                // 🔴 CHANGED
                width: '100%',
                height: '100%',

                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',

                // 🔴 CHANGED - removed px: 3
                color: 'var(--text-muted)',
                fontSize: 16,
                fontWeight: 500,
                whiteSpace: 'nowrap',
              }}
            >
              None — No Data Found
            </Box>
          ) : (
            <Box className="lw-marquee-track">
              {[...excursions, ...excursions].map((e, i) => {
                const eStatus = effectiveStatus(e)
                const eMeta = STATUS_META[eStatus]
                return (
                  <Box
                    key={`${e.uuid || e.name}-${i}`}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',

                      // 🔴 CHANGED
                      justifyContent: 'center',
                      height: '100%',

                      gap: 1.75,
                      fontSize: 16,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        px: 1.4,
                        py: 0.5,
                        borderRadius: 1,
                        backgroundColor: eMeta.soft,
                        color: eMeta.color,
                        border: `1px solid ${eMeta.color}`,
                        fontSize: 11,
                        fontWeight: 800,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        lineHeight: 1,
                      }}
                    >
                      {eStatus === 'crit' ? 'CRITICAL' : 'WARNING'}
                    </Box>
                    <Box
                      sx={{
                        color: 'var(--heading-color)',
                        fontWeight: 700,
                        fontSize: 16,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {e.name || 'Zone'}
                    </Box>
                    <Box
                      sx={{
                        color: 'var(--text-muted)',
                        fontSize: 15,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {excursionLine(e)}
                    </Box>
                  </Box>
                )
              })}
            </Box>
          )}
        </Box>

        {/* Refresh + Open full app — sit on the right of the bar */}
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: 2,
            borderLeft: '1px solid var(--divider-color)',
          }}
        >
          {isFetching && (
            <Typography
              variant="caption"
              sx={{ color: 'var(--text-muted)', fontSize: 11 }}
              className="lw-pulse"
            >
              refreshing…
            </Typography>
          )}
          <Button
            size="small"
            variant="outlinedNeutral"
            startIcon={<RefreshCw size={14} />}
            onClick={() => refetch()}
            disabled={isFetching}
          >
            Refresh
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<ExternalLink size={14} />}
            href="/login"
          >
            Open full app
          </Button>
          {/* Hidden back button — keeps history navigation for keyboard users
              who focus this row before tabbing to the action buttons. */}
          <Button
            size="small"
            variant="outlinedNeutral"
            startIcon={<ArrowLeft size={14} />}
            onClick={() => window.history.length > 1 && window.history.back()}
            sx={{ display: 'none' }}
          >
            Back
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default LiveWall
